import React from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Inbox, Users, Settings, LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";

const DEFAULT_LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_agent-hub-375/artifacts/ijuy74i5_LOGO%20DOPOPAY.webp";

const NAV = [
  { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/richieste", icon: Inbox, label: "Richieste" },
  { to: "/admin/agenti", icon: Users, label: "Agenti" },
  { to: "/admin/impostazioni", icon: Settings, label: "Impostazioni" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!loading && !user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#111111]">
        <div className="w-10 h-10 border-4 border-[#FFCC00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isAdmin = user.role === "admin";
  const nav = NAV.filter((n) => n.label !== "Impostazioni" || isAdmin);

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#111111] text-white flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <img src={DEFAULT_LOGO} alt="Dopopay" className="h-6" />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${isActive ? "bg-[#FFCC00] text-black" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
              <n.icon className="w-5 h-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <img src={user.picture || DEFAULT_LOGO} alt="" className="w-9 h-9 rounded-full object-cover bg-white/10" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-white/50 capitalize">{user.role}</p>
            </div>
          </div>
          <Button onClick={logout} data-testid="logout-btn" variant="ghost"
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10">
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-5 gap-3 lg:hidden sticky top-0 z-20">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
          <img src={DEFAULT_LOGO} alt="Dopopay" className="h-5 invert" />
        </header>
        <main className="p-5 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
